<template>
  <div>
    <template v-if="!hasPremiumFeaturesEnabled">
      <div class="row-comments">
        <div class="row-comments__empty">
          <i class="row-comments__empty-icon iconoir-multi-bubble"></i>
          <div class="row-comments__empty-text">
            {{ $t('rowCommentSidebar.onlyPremium') }}
          </div>
          <Button
            type="primary"
            icon="iconoir-no-lock"
            @click="$refs.paidFeaturesModal.show()"
          >
            {{ $t('rowCommentSidebar.more') }}
          </Button>
        </div>
        <PaidFeaturesModal
          ref="paidFeaturesModal"
          initial-selected-type="row_comments"
          :workspace="workspace"
        ></PaidFeaturesModal>
      </div>
    </template>
    <template v-else>
      <div v-if="!loaded && loading" class="loading-absolute-center" />
      <!-- Origem Flow: soltar arquivos em qualquer ponto da conversa anexa
           no comentário (linha 16 da tabela de ajustes). -->
      <div
        v-else
        class="row-comments-dropzone"
        @dragenter.prevent="onFileDragEnter"
        @dragover.prevent="onFileDragOver"
        @dragleave="onFileDragLeave"
        @drop.prevent="onFileDrop"
      >
        <div
          v-if="fileDragDepth > 0 && canCreateComments"
          class="row-comments-dropzone__overlay"
        >
          <i class="iconoir-attachment"></i>
          {{ $t('rowCommentSidebar.dropToAttach') }}
        </div>
        <div class="row-comments">
          <div v-if="currentCount === 0" class="row-comments__empty">
            <i class="row-comments__empty-icon iconoir-multi-bubble"></i>
            <div class="row-comments__empty-text">
              <template v-if="!canCreateComments">{{
                $t('rowCommentSidebar.readOnlyNoComment')
              }}</template>
              <template v-else>
                {{ $t('rowCommentSidebar.noComment') }}
              </template>
            </div>
          </div>
          <div v-else class="row-comments__body">
            <InfiniteScroll
              ref="infiniteScroll"
              :current-count="currentCount"
              :max-count="totalCount"
              :loading="loading"
              :reverse="true"
              :render-end="false"
              @load-next-page="nextPage"
            >
              <template #default>
                <div
                  v-for="(c, index) in comments"
                  :key="'row-comment-' + c.id"
                >
                  <div
                    v-if="
                      shouldDisplayDateSeparator(comments, 'created_on', index)
                    "
                    class="row-comment__day-separator"
                  >
                    <span>{{ formatDateSeparator(c.created_on) }}</span>
                  </div>
                  <RowComment
                    :comment="c"
                    :workspace="workspace"
                    :can-edit="canEditComments"
                    :can-delete="canDeleteComments"
                    :view-id="view ? view.id : null"
                  />
                </div>
              </template>
            </InfiniteScroll>
          </div>
          <div v-if="canCreateComments" class="row-comments__foot">
            <!-- Origem Flow: compositor estilo ClickUp — editor + barra de
                 ferramentas (menção, emoji, anexo, áudio, clipe) e enviar.
                 O paste em captura intercepta prints/arquivos colados
                 (Ctrl/Cmd-V) antes do ProseMirror e anexa no comentário. -->
            <div class="comment-composer" @paste.capture="onComposerPaste">
              <!-- Chip do item de ação escolhido para o próximo comentário -->
              <div
                v-if="actionItemAssignee"
                class="comment-composer__action-chip"
              >
                <i class="iconoir-add-user"></i>
                <span>{{
                  $t('rowCommentSidebar.actionItemFor', {
                    name: actionItemAssignee.name,
                  })
                }}</span>
                <a
                  class="comment-composer__action-chip-remove"
                  @click.prevent="actionItemAssignee = null"
                >
                  <i class="iconoir-cancel"></i>
                </a>
              </div>
              <RichTextEditor
                ref="editor"
                v-model="comment"
                editor-class="rich-text-editor__content--comment"
                :mentionable-users="workspace.users"
                :enable-links="true"
                :enable-images="true"
                :placeholder="$t('rowCommentSidebar.comment')"
                :enter-stop-edit="true"
                @stop-edit="postComment()"
              />
              <div class="comment-composer__toolbar">
                <a
                  v-tooltip="$t('rowCommentSidebar.toolMention')"
                  class="comment-composer__tool"
                  @click.prevent="insertMention()"
                >
                  <i class="iconoir-at-sign"></i>
                </a>
                <a
                  ref="emojiLink"
                  v-tooltip="$t('rowCommentSidebar.toolEmoji')"
                  class="comment-composer__tool"
                  @click.prevent="toggleEmojiPicker()"
                >
                  <i class="iconoir-emoji"></i>
                </a>
                <a
                  ref="assigneeLink"
                  v-tooltip="$t('rowCommentSidebar.toolActionItem')"
                  class="comment-composer__tool"
                  :class="{
                    'comment-composer__tool--active': actionItemAssignee,
                  }"
                  @click.prevent="toggleAssigneePicker()"
                >
                  <i class="iconoir-add-user"></i>
                </a>
                <a
                  ref="taskLink"
                  v-tooltip="$t('rowCommentSidebar.toolTaskMention')"
                  class="comment-composer__tool"
                  @click.prevent="toggleTaskPicker()"
                >
                  <i class="iconoir-task-list"></i>
                </a>
                <a
                  v-tooltip="$t('rowCommentSidebar.toolAttach')"
                  class="comment-composer__tool"
                  :class="{ 'comment-composer__tool--disabled': uploading }"
                  @click.prevent="$refs.fileInput.click()"
                >
                  <i class="iconoir-attachment"></i>
                </a>
                <a
                  v-if="screenCaptureSupported"
                  v-tooltip="$t('rowCommentSidebar.toolScreenshot')"
                  class="comment-composer__tool"
                  :class="{
                    'comment-composer__tool--disabled':
                      recordingType !== null || uploading,
                  }"
                  @click.prevent="captureScreenshot()"
                >
                  <i class="iconoir-screenshot"></i>
                </a>
                <a
                  v-if="driveEnabled"
                  v-tooltip="$t('rowCommentSidebar.toolDrive')"
                  class="comment-composer__tool"
                  :class="{ 'comment-composer__tool--disabled': uploading }"
                  @click.prevent="attachFromDrive()"
                >
                  <i class="iconoir-google-drive"></i>
                </a>
                <a
                  ref="kudosLink"
                  v-tooltip="$t('rowCommentSidebar.toolKudos')"
                  class="comment-composer__tool"
                  @click.prevent="openKudos"
                >
                  <i class="iconoir-heart"></i>
                </a>
                <a
                  v-tooltip="
                    recordingType === 'audio'
                      ? $t('rowCommentSidebar.toolStop')
                      : $t('rowCommentSidebar.toolAudio')
                  "
                  class="comment-composer__tool"
                  :class="{
                    'comment-composer__tool--recording':
                      recordingType === 'audio',
                    'comment-composer__tool--disabled':
                      recordingType === 'clip' || uploading,
                  }"
                  @click.prevent="toggleRecording('audio')"
                >
                  <i
                    :class="
                      recordingType === 'audio'
                        ? 'iconoir-square'
                        : 'iconoir-mic'
                    "
                  ></i>
                  <span v-if="recordingType === 'audio'">{{
                    recordingTime
                  }}</span>
                </a>
                <a
                  v-tooltip="
                    recordingType === 'clip'
                      ? $t('rowCommentSidebar.toolStop')
                      : $t('rowCommentSidebar.toolClip')
                  "
                  class="comment-composer__tool"
                  :class="{
                    'comment-composer__tool--recording':
                      recordingType === 'clip',
                    'comment-composer__tool--disabled':
                      recordingType === 'audio' || uploading,
                  }"
                  @click.prevent="toggleRecording('clip')"
                >
                  <i
                    :class="
                      recordingType === 'clip'
                        ? 'iconoir-square'
                        : 'iconoir-video-camera'
                    "
                  ></i>
                  <span v-if="recordingType === 'clip'">{{
                    recordingTime
                  }}</span>
                </a>
                <span
                  v-if="uploading"
                  class="comment-composer__uploading"
                  >{{ $t('rowCommentSidebar.uploading') }}</span
                >
                <a
                  v-tooltip="$t('rowCommentSidebar.send')"
                  class="comment-composer__send"
                  @click.prevent="postComment()"
                >
                  <i class="iconoir-arrow-up"></i>
                </a>
              </div>
              <input
                ref="fileInput"
                type="file"
                multiple
                class="comment-composer__file-input"
                @change="onFilesChosen"
              />
              <ScreenshotCropOverlay
                v-if="screenshotSource"
                :source="screenshotSource"
                @confirm="attachScreenshot"
                @cancel="screenshotSource = null"
              />
              <!-- Origem Flow (H4): kudos para um colega, ligado à tarefa. -->
              <Context ref="kudosContext">
                <div class="comment-composer__kudos">
                  <div class="home-kudos__form-title">
                    👏 {{ $t('rowCommentSidebar.kudosTitle') }}
                  </div>
                  <Dropdown v-model="kudosToUserId" :show-search="false">
                    <DropdownItem
                      v-for="user in kudosColleagues"
                      :key="user.user_id"
                      :name="user.name"
                      :value="user.user_id"
                    ></DropdownItem>
                  </Dropdown>
                  <input
                    v-model="kudosMessage"
                    class="input"
                    :placeholder="$t('rowCommentSidebar.kudosPlaceholder')"
                    maxlength="280"
                    @keydown.enter.prevent="giveKudos"
                  />
                  <Button
                    type="primary"
                    :loading="kudosSaving"
                    :disabled="!kudosToUserId || kudosSaving"
                    @click="giveKudos"
                  >
                    {{ $t('rowCommentSidebar.kudosSend') }}
                  </Button>
                </div>
              </Context>
              <Context
                ref="emojiContext"
                overflow-scroll
                max-height-if-outside-viewport
              >
                <div class="comment-composer__emojis">
                  <a
                    v-for="emoji in emojis"
                    :key="emoji"
                    class="comment-composer__emoji"
                    @click.prevent="insertEmoji(emoji)"
                    >{{ emoji }}</a
                  >
                </div>
              </Context>
              <Context
                ref="assigneeContext"
                overflow-scroll
                max-height-if-outside-viewport
              >
                <div class="comment-composer__picker">
                  <div class="comment-composer__picker-title">
                    {{ $t('rowCommentSidebar.actionItemTitle') }}
                  </div>
                  <a
                    v-for="user in workspace.users"
                    :key="'assignee-' + user.user_id"
                    class="comment-composer__picker-item"
                    @click.prevent="selectAssignee(user)"
                  >
                    <img
                      v-if="user.avatar_url"
                      class="comment-composer__picker-photo"
                      :src="user.avatar_url"
                      :alt="user.name"
                    />
                    <span v-else class="comment-composer__picker-initial">{{
                      (user.name || '?').substring(0, 1).toUpperCase()
                    }}</span>
                    <span class="comment-composer__picker-name">{{
                      user.name
                    }}</span>
                    <i
                      v-if="
                        actionItemAssignee &&
                        actionItemAssignee.user_id === user.user_id
                      "
                      class="iconoir-check comment-composer__picker-check"
                    ></i>
                  </a>
                </div>
              </Context>
              <Context
                ref="taskContext"
                overflow-scroll
                max-height-if-outside-viewport
              >
                <div class="comment-composer__picker">
                  <div class="comment-composer__picker-title">
                    {{ $t('rowCommentSidebar.taskMentionTitle') }}
                  </div>
                  <input
                    ref="taskSearchInput"
                    v-model="taskSearch"
                    class="comment-composer__picker-search"
                    :placeholder="$t('rowCommentSidebar.taskMentionSearch')"
                    @input="scheduleTaskSearch()"
                  />
                  <div
                    v-if="taskSearchLoading"
                    class="comment-composer__picker-empty"
                  >
                    {{ $t('rowCommentSidebar.taskMentionLoading') }}
                  </div>
                  <div
                    v-else-if="taskResults.length === 0"
                    class="comment-composer__picker-empty"
                  >
                    {{ $t('rowCommentSidebar.taskMentionEmpty') }}
                  </div>
                  <a
                    v-for="task in taskResults"
                    :key="'task-' + task.id"
                    class="comment-composer__picker-item"
                    @click.prevent="insertTaskMention(task)"
                  >
                    <i class="iconoir-task-list"></i>
                    <span class="comment-composer__picker-name">{{
                      task.title
                    }}</span>
                  </a>
                </div>
              </Context>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { markRaw } from 'vue'
import { mapGetters } from 'vuex'
import {
  shouldDisplayDateSeparator,
  formatDateSeparator,
} from '@baserow/modules/database/utils/date'
import { notifyIf } from '@baserow/modules/core/utils/error'
import UserFileService from '@baserow/modules/core/services/userFile'
import ClipsService from '@baserow/modules/core/services/clips'
import KudosService from '@baserow/modules/core/services/kudos'
import RowComment from '@baserow_premium/components/row_comments/RowComment'
import ScreenshotCropOverlay from '@baserow_premium/components/row_comments/ScreenshotCropOverlay'
import {
  captureScreenFrame,
  cropCanvasToFile,
  isScreenCaptureCancel,
  isScreenCaptureSupported,
} from '@baserow_premium/utils/screenCapture'
import InfiniteScroll from '@baserow/modules/core/components/helpers/InfiniteScroll'
import RichTextEditor from '@baserow/modules/core/components/editor/RichTextEditor.vue'
import PremiumFeatures from '@baserow_premium/features'
import PaidFeaturesModal from '@baserow_premium/components/PaidFeaturesModal'

export default {
  name: 'RowCommentsSidebar',
  components: {
    PaidFeaturesModal,
    InfiniteScroll,
    RowComment,
    RichTextEditor,
    ScreenshotCropOverlay,
  },
  props: {
    database: {
      type: Object,
      required: true,
    },
    table: {
      required: true,
      type: Object,
    },
    row: {
      required: true,
      type: Object,
    },
    view: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      kudosToUserId: null,
      kudosMessage: '',
      kudosSaving: false,
      comment: '',
      uploading: false,
      // Origem Flow: profundidade do dragenter de arquivos sobre a
      // conversa (0 = nenhum arrasto em andamento).
      fileDragDepth: 0,
      // 'audio' | 'clip' | null — só uma gravação por vez.
      recordingType: null,
      recordingTime: '0:00',
      // Captura de tela: só no cliente e onde o navegador oferece a API
      // (celular não tem); o canvas capturado aguarda o recorte.
      screenCaptureSupported: false,
      screenshotSource: null,
      // Item de ação: usuário selecionado para o próximo comentário.
      actionItemAssignee: null,
      // Mencionar tarefa: busca no painel.
      taskSearch: '',
      taskResults: [],
      taskSearchLoading: false,
      // Emojis mais usados, no espírito do picker do ClickUp.
      emojis: [
        '😀',
        '😁',
        '😂',
        '🤣',
        '😊',
        '😍',
        '😅',
        '😉',
        '🙃',
        '🤔',
        '😴',
        '😢',
        '😭',
        '😡',
        '🤯',
        '🥳',
        '👍',
        '👎',
        '👏',
        '🙏',
        '🙌',
        '💪',
        '🤝',
        '👀',
        '✅',
        '❌',
        '⚠️',
        '⭐',
        '🔥',
        '🎉',
        '🚀',
        '❤️',
        '💯',
        '💡',
        '📌',
        '📎',
        '📅',
        '⏰',
        '🐛',
        '⚡',
        '☕',
        '🏆',
      ],
    }
  },
  mounted() {
    this.screenCaptureSupported = isScreenCaptureSupported()
  },
  beforeUnmount() {
    this.stopMediaTracks()
    clearInterval(this.recordingInterval)
    clearTimeout(this.recordingMaxTimeout)
    clearTimeout(this.taskSearchTimeout)
  },
  computed: {
    kudosColleagues() {
      const workspace = this.$store.getters['workspace/getSelected'] || {}
      const me = this.$store.getters['auth/getUserId']
      return (workspace.users || []).filter((u) => u.user_id !== me)
    },
    hasPremiumFeaturesEnabled() {
      return this.$hasFeature(
        PremiumFeatures.PREMIUM,
        this.database.workspace.id
      )
    },
    ...mapGetters({
      loggedUserId: 'auth/getUserId',
      comments: 'row_comments/getSortedRowComments',
      loading: 'row_comments/getLoading',
      loaded: 'row_comments/getLoaded',
      currentCount: 'row_comments/getCurrentCount',
      totalCount: 'row_comments/getTotalCount',
      additionalUserData: 'auth/getAdditionalUserData',
    }),
    canCreateComments() {
      return (
        this.$hasPermission(
          'database.table.create_comment',
          this.table,
          this.database.workspace.id
        ) ||
        (this.view &&
          this.$hasPermission(
            'database.table.view.create_comment',
            this.view,
            this.database.workspace.id
          ))
      )
    },
    canEditComments() {
      return (
        this.$hasPermission(
          'database.table.update_comment',
          this.table,
          this.database.workspace.id
        ) ||
        (this.view &&
          this.$hasPermission(
            'database.table.view.update_comment',
            this.view,
            this.database.workspace.id
          ))
      )
    },
    canDeleteComments() {
      return (
        this.$hasPermission(
          'database.table.delete_comment',
          this.table,
          this.database.workspace.id
        ) ||
        (this.view &&
          this.$hasPermission(
            'database.table.view.delete_comment',
            this.view,
            this.database.workspace.id
          ))
      )
    },
    workspace() {
      return this.$store.getters['workspace/get'](this.database.workspace.id)
    },
    driveEnabled() {
      return !!this.$googleDrive?.enabled
    },
  },
  watch: {
    async hasPremiumFeaturesEnabled() {
      await this.initialLoad()
    },
    'row.id': {
      handler() {
        this.initialLoad()
      },
    },
  },
  async created() {
    await this.initialLoad()
  },
  methods: {
    openKudos() {
      // Sugere o responsável da tarefa quando houver campo de colaboradores.
      if (!this.kudosToUserId) {
        const collab = Object.keys(this.row || {})
          .filter((k) => k.startsWith('field_'))
          .map((k) => this.row[k])
          .find((v) => Array.isArray(v) && v.length && v[0] && v[0].id && v[0].name)
        const me = this.$store.getters['auth/getUserId']
        const first = collab ? collab.find((u) => u.id !== me) : null
        if (first) this.kudosToUserId = first.id
      }
      this.$refs.kudosContext.toggle(this.$refs.kudosLink, 'top', 'left', 4)
    },
    async giveKudos() {
      if (!this.kudosToUserId) return
      this.kudosSaving = true
      try {
        const workspace = this.$store.getters['workspace/getSelected']
        await KudosService(this.$client).give(workspace.id, {
          to_user_id: this.kudosToUserId,
          message: this.kudosMessage,
          table_id: this.table.id,
          row_id: this.row.id,
        })
        this.kudosMessage = ''
        this.$refs.kudosContext.hide()
        this.$store.dispatch('toast/success', {
          title: this.$t('rowCommentSidebar.kudosSentTitle'),
          message: this.$t('rowCommentSidebar.kudosSentText'),
        })
        window.dispatchEvent(new CustomEvent('origem-kudos-given'))
      } catch (error) {
        notifyIf(error, 'kudos')
      }
      this.kudosSaving = false
    },
    async initialLoad() {
      if (!this.hasPremiumFeaturesEnabled) {
        return
      }

      try {
        const tableId = this.table.id
        const rowId = this.row.id
        const viewId = this.view?.id

        // If the row is not an integer, it can mean that the row hasn't been created
        // in the backend yet. It's fine to not do anything then, because there are no
        // comments anyway.
        if (Number.isInteger(rowId)) {
          await this.$store.dispatch('row_comments/fetchRowComments', {
            tableId,
            rowId,
            viewId,
          })
        }
      } catch (e) {
        notifyIf(e, 'application')
      }
    },
    async postComment() {
      const comment = this.comment
      if (!comment || !this.canCreateComments) {
        return
      }
      const assignee = this.actionItemAssignee?.user_id ?? null
      try {
        this.comment = ''
        this.actionItemAssignee = null
        await this.$store.dispatch('row_comments/postComment', {
          tableId: this.table.id,
          rowId: this.row.id,
          message: comment,
          viewId: this.view?.id,
          assignee,
        })
        this.$refs.infiniteScroll?.scrollToStart()
      } catch (e) {
        notifyIf(e, 'application')
      }
    },
    async nextPage() {
      try {
        await this.$store.dispatch('row_comments/fetchNextSetOfComments', {
          tableId: this.table.id,
          rowId: this.row.id,
          viewId: this.view?.id,
        })
      } catch (e) {
        notifyIf(e, 'application')
      }
    },
    shouldDisplayDateSeparator,
    formatDateSeparator,
    // --- Origem Flow: barra de ferramentas do compositor (estilo ClickUp) ---
    editorInstance() {
      return this.$refs.editor?.editor || null
    },
    insertMention() {
      const editor = this.editorInstance()
      if (!editor) {
        return
      }
      // O plugin de sugestão do TipTap só dispara com o "@" no começo de uma
      // palavra, então garante um espaço antes quando o cursor segue texto.
      const { from } = editor.state.selection
      const charBefore =
        from > 0 ? editor.state.doc.textBetween(from - 1, from) : ''
      const prefix = charBefore && charBefore !== ' ' ? ' @' : '@'
      editor.chain().focus().insertContent(prefix).run()
    },
    toggleEmojiPicker() {
      this.$refs.emojiContext.toggle(this.$refs.emojiLink, 'top', 'left', 4)
    },
    toggleAssigneePicker() {
      this.$refs.assigneeContext.toggle(
        this.$refs.assigneeLink,
        'top',
        'left',
        4
      )
    },
    selectAssignee(user) {
      this.actionItemAssignee =
        this.actionItemAssignee?.user_id === user.user_id ? null : user
      this.$refs.assigneeContext.hide()
    },
    toggleTaskPicker() {
      this.$refs.taskContext.toggle(this.$refs.taskLink, 'top', 'left', 4)
      if (this.taskResults.length === 0) {
        this.searchTasks()
      }
      this.$nextTick(() => this.$refs.taskSearchInput?.focus())
    },
    scheduleTaskSearch() {
      clearTimeout(this.taskSearchTimeout)
      this.taskSearchTimeout = setTimeout(() => this.searchTasks(), 300)
    },
    async searchTasks() {
      this.taskSearchLoading = true
      try {
        const search = this.taskSearch.trim()
        const params = new URLSearchParams({ size: '8' })
        if (search) {
          params.set('search', search)
        }
        const { data } = await this.$client.get(
          `/database/rows/table/${this.table.id}/?${params.toString()}`
        )
        const primary = this.$store.getters['field/getAll'].find(
          (field) => field.primary
        )
        this.taskResults = (data.results || []).map((row) => ({
          id: row.id,
          title:
            (primary && row[`field_${primary.id}`]) ||
            `${this.table.name} #${row.id}`,
        }))
      } catch (error) {
        this.taskResults = []
      } finally {
        this.taskSearchLoading = false
      }
    },
    insertTaskMention(task) {
      const base = window.location.pathname.replace(/\/row\/\d+$/, '')
      this.insertChipLink(`⊙ ${task.title}`, `${base}/row/${task.id}`)
      this.$refs.taskContext.hide()
    },
    insertChipLink(label, href) {
      const editor = this.editorInstance()
      if (!editor) {
        return
      }
      editor
        .chain()
        .focus()
        .insertContent([
          {
            type: 'text',
            text: label,
            marks: [
              { type: 'link', attrs: { href, class: 'task-mention' } },
            ],
          },
          { type: 'text', text: ' ' },
        ])
        .run()
    },
    insertEmoji(emoji) {
      const editor = this.editorInstance()
      if (editor) {
        editor.chain().focus().insertContent(emoji).run()
      }
      this.$refs.emojiContext.hide()
    },
    insertLinkText(label, url) {
      const editor = this.editorInstance()
      if (!editor) {
        return
      }
      editor
        .chain()
        .focus()
        .insertContent([
          {
            type: 'text',
            text: label,
            marks: [
              {
                type: 'link',
                attrs: { href: url, class: 'comment-attachment' },
              },
            ],
          },
          { type: 'text', text: ' ' },
        ])
        .run()
    },
    /**
     * Origem Flow: imagens (print colado, arquivo solto ou anexado) entram
     * como nó de imagem inline — o comentário mostra a própria imagem em vez
     * de um link.
     */
    insertImageNode(data) {
      const editor = this.editorInstance()
      if (!editor) {
        return
      }
      editor
        .chain()
        .focus()
        .insertContent([
          {
            type: 'image',
            attrs: { src: data.url, alt: data.original_name },
          },
          { type: 'text', text: ' ' },
        ])
        .run()
    },
    /**
     * Origem Flow: Ctrl/Cmd-V com print ou arquivo no clipboard anexa no
     * comentário (linha 34 da tabela de ajustes). Captura antes do
     * ProseMirror; colar texto segue o fluxo normal.
     */
    async onComposerPaste(event) {
      const files = [...(event.clipboardData?.files || [])]
      if (files.length === 0 || !this.canCreateComments) {
        return
      }
      event.preventDefault()
      event.stopPropagation()
      await this.uploadAndInsertFiles(files)
    },
    async onFilesChosen(event) {
      const files = [...(event.target.files || [])]
      event.target.value = ''
      await this.uploadAndInsertFiles(files)
    },
    /**
     * Origem Flow: ícone (emoji) pelo tipo do arquivo — Excel/planilha,
     * PDF, Word, apresentação, imagem, vídeo, áudio, compactado — no lugar
     * do clipe genérico (linha 16 da tabela de ajustes).
     */
    fileTypeIcon(name) {
      const extension = (name || '').split('.').pop().toLowerCase()
      const byExtension = {
        xls: '📗',
        xlsx: '📗',
        csv: '📗',
        ods: '📗',
        pdf: '📕',
        doc: '📘',
        docx: '📘',
        odt: '📘',
        rtf: '📘',
        ppt: '📙',
        pptx: '📙',
        odp: '📙',
        png: '🖼️',
        jpg: '🖼️',
        jpeg: '🖼️',
        gif: '🖼️',
        webp: '🖼️',
        svg: '🖼️',
        mp4: '🎬',
        mov: '🎬',
        avi: '🎬',
        webm: '🎬',
        mkv: '🎬',
        mp3: '🎧',
        wav: '🎧',
        ogg: '🎧',
        m4a: '🎧',
        zip: '🗜️',
        rar: '🗜️',
        '7z': '🗜️',
        'tar': '🗜️',
        gz: '🗜️',
      }
      return byExtension[extension] || '📎'
    },
    async uploadAndInsertFiles(files) {
      if (files.length === 0) {
        return
      }
      this.uploading = true
      try {
        for (const file of files) {
          const { data } = await UserFileService(this.$client).uploadFile(file)
          if ((file.type || '').startsWith('image/')) {
            this.insertImageNode(data)
          } else {
            this.insertLinkText(
              `${this.fileTypeIcon(data.original_name)} ${data.original_name}`,
              data.url
            )
          }
        }
      } catch (error) {
        notifyIf(error, 'userFile')
      } finally {
        this.uploading = false
      }
    },
    isFileDrag(event) {
      const types = event.dataTransfer?.types
      return !!types && [...types].includes('Files')
    },
    onFileDragEnter(event) {
      if (!this.isFileDrag(event) || !this.canCreateComments) {
        return
      }
      this.fileDragDepth += 1
    },
    onFileDragOver(event) {
      if (!this.isFileDrag(event) || !this.canCreateComments) {
        return
      }
      event.dataTransfer.dropEffect = 'copy'
    },
    onFileDragLeave(event) {
      if (!this.isFileDrag(event)) {
        return
      }
      this.fileDragDepth = Math.max(0, this.fileDragDepth - 1)
    },
    async onFileDrop(event) {
      this.fileDragDepth = 0
      if (!this.canCreateComments || !this.isFileDrag(event)) {
        return
      }
      const files = [...(event.dataTransfer.files || [])]
      await this.uploadAndInsertFiles(files)
    },
    // Origem Flow: anexa arquivos do Google Drive como chips de link no
    // comentário (referência viva, como no ClickUp — não copia o arquivo).
    async attachFromDrive() {
      try {
        const docs = await this.$googleDrive.pickFiles({ multiselect: true })
        for (const doc of docs) {
          this.insertChipLink(`📄 ${doc.name}`, doc.url)
        }
      } catch (error) {
        this.$store.dispatch('toast/error', {
          title: this.$t('rowCommentSidebar.driveErrorTitle'),
          message: this.$t('rowCommentSidebar.driveError'),
        })
      }
    },
    stopMediaTracks() {
      ;(this.activeStreams || []).forEach((stream) => {
        stream.getTracks().forEach((track) => track.stop())
      })
      this.activeStreams = []
    },
    formatRecordingTime(ms) {
      const totalSeconds = Math.floor(ms / 1000)
      const minutes = Math.floor(totalSeconds / 60)
      const seconds = `${totalSeconds % 60}`.padStart(2, '0')
      return `${minutes}:${seconds}`
    },
    async toggleRecording(type) {
      if (this.recordingType === type) {
        this.recorder?.stop()
        return
      }
      if (this.recordingType !== null || this.uploading) {
        return
      }
      if (
        typeof navigator === 'undefined' ||
        !navigator.mediaDevices ||
        (type === 'clip' && !navigator.mediaDevices.getDisplayMedia)
      ) {
        this.showRecordingError(type)
        return
      }
      try {
        let stream
        if (type === 'audio') {
          stream = await navigator.mediaDevices.getUserMedia({ audio: true })
          this.activeStreams = [stream]
        } else {
          const screenStream = await navigator.mediaDevices.getDisplayMedia({
            video: true,
            audio: true,
          })
          this.activeStreams = [screenStream]
          // Tenta misturar o microfone na narração do clipe; sem permissão o
          // clipe segue só com o áudio da tela.
          try {
            const micStream = await navigator.mediaDevices.getUserMedia({
              audio: true,
            })
            this.activeStreams.push(micStream)
            stream = new MediaStream([
              ...screenStream.getVideoTracks(),
              ...screenStream.getAudioTracks(),
              ...micStream.getAudioTracks(),
            ])
          } catch (error) {
            stream = screenStream
          }
          // Encerrar o compartilhamento pela barra do navegador também para a
          // gravação.
          screenStream.getVideoTracks()[0].onended = () => {
            if (this.recordingType === 'clip') {
              this.recorder?.stop()
            }
          }
        }
        const mimeType =
          type === 'audio'
            ? ['audio/webm;codecs=opus', 'audio/webm'].find((t) =>
                MediaRecorder.isTypeSupported(t)
              )
            : ['video/webm;codecs=vp9,opus', 'video/webm'].find((t) =>
                MediaRecorder.isTypeSupported(t)
              )
        this.recorder = new MediaRecorder(
          stream,
          mimeType ? { mimeType } : undefined
        )
        this.recordedChunks = []
        this.recordingStartedAt = Date.now()
        this.recordingType = type
        this.recordingTime = '0:00'
        this.recorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            this.recordedChunks.push(event.data)
          }
        }
        this.recorder.onstop = () => this.finishRecording(type)
        this.recorder.start(1000)
        this.recordingInterval = setInterval(() => {
          this.recordingTime = this.formatRecordingTime(
            Date.now() - this.recordingStartedAt
          )
        }, 500)
        // Limite de segurança de 10 minutos, como no hub de Clipes.
        this.recordingMaxTimeout = setTimeout(() => {
          this.recorder?.stop()
        }, 10 * 60 * 1000)
      } catch (error) {
        this.stopMediaTracks()
        this.showRecordingError(type)
      }
    },
    async finishRecording(type) {
      clearInterval(this.recordingInterval)
      clearTimeout(this.recordingMaxTimeout)
      const duration = this.formatRecordingTime(
        Date.now() - this.recordingStartedAt
      )
      this.recordingType = null
      this.stopMediaTracks()
      const chunks = this.recordedChunks
      this.recordedChunks = []
      if (chunks.length === 0) {
        return
      }
      const isAudio = type === 'audio'
      const blob = new Blob(chunks, {
        type: isAudio ? 'audio/webm' : 'video/webm',
      })
      const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-')
      const file = new File(
        [blob],
        `${isAudio ? 'audio' : 'clipe'}-${stamp}.webm`,
        { type: blob.type }
      )
      this.uploading = true
      try {
        const label = isAudio
          ? `🎤 ${this.$t('rowCommentSidebar.audioLabel')} (${duration})`
          : `🎥 ${this.$t('rowCommentSidebar.clipLabel')} (${duration})`
        if (isAudio) {
          const { data } = await UserFileService(this.$client).uploadFile(file)
          this.insertLinkText(label, data.url)
        } else {
          // Origem Flow: o vídeo vai para o diretório privado de clipes e o
          // comentário linka o hub de Clipes (rota do app, exige login) — a
          // URL pública eterna de user-files não existe mais para clipes.
          const { data: clip } = await ClipsService(this.$client).upload(
            this.workspace.id,
            file,
            `${this.$t('rowCommentSidebar.clipLabel')} — ${this.table.name}`
          )
          const clipRoute = this.$router.resolve({
            name: 'workspace-clips',
            params: { workspaceId: this.workspace.id },
            query: { clip: clip.id },
          })
          this.insertLinkText(
            label,
            new URL(clipRoute.href, window.location.origin).toString()
          )
        }
        this.$store.dispatch('toast/success', {
          title: this.$t('rowCommentSidebar.recordingAttachedTitle'),
          message: this.$t('rowCommentSidebar.recordingAttached'),
        })
      } catch (error) {
        notifyIf(error, 'userFile')
      } finally {
        this.uploading = false
      }
    },
    /**
     * Origem Flow: print de tela direto do compositor. O navegador abre o
     * seletor (aba, janela ou tela), um quadro é capturado e o usuário
     * recorta a área antes de anexar.
     */
    async captureScreenshot() {
      if (this.recordingType !== null || this.uploading) {
        return
      }
      try {
        this.screenshotSource = markRaw(await captureScreenFrame())
      } catch (error) {
        if (!isScreenCaptureCancel(error)) {
          this.showRecordingError('screenshot')
        }
      }
    },
    async attachScreenshot(selection) {
      const source = this.screenshotSource
      this.screenshotSource = null
      const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-')
      try {
        const file = await cropCanvasToFile(
          source,
          selection,
          `captura-${stamp}.png`
        )
        await this.uploadAndInsertFiles([file])
      } catch (error) {
        this.showRecordingError('screenshot')
      }
    },
    showRecordingError(type) {
      if (type === 'screenshot') {
        this.$store.dispatch('toast/error', {
          title: this.$t('rowCommentSidebar.screenshotErrorTitle'),
          message: this.$t('rowCommentSidebar.screenshotError'),
        })
        return
      }
      this.$store.dispatch('toast/error', {
        title: this.$t('rowCommentSidebar.recordingErrorTitle'),
        message:
          type === 'audio'
            ? this.$t('rowCommentSidebar.micError')
            : this.$t('rowCommentSidebar.screenError'),
      })
    },
  },
}
</script>
